// Curated TTS catalog — defined entirely in the frontend.
//
// Voices + accents are hand-maintained reference data. The backend only synthesizes. The front sends
// the picked voice's id + provider + accent locale, and the backend renders accordingly.
//
// "Advanced" voices (everything that is not Microsoft) are shown only when the tenant has
// ENABLE_ADVANCED_TTS on (carried in the edition JWT). Microsoft is the only free tier.
//
// KEEP IN SYNC with speacher_video_transcode/src/tts/preview/voiceCatalog.ts (voice ids per
// provider, accent locales + base, accent providers). The backend's trimmed copy drives the
// voice-preview pre-generation at startup. Drift = a preview MP3 missing on disk -> frontend
// silently 404s with no audio. A matching comment on the backend file points back here.

export type TTSProviderId = "MICROSOFT" | "GEMINI";
export type VoiceGender = "MALE" | "FEMALE" | "NEUTRAL";

export interface VoiceDescriptor {
  id: string;
  label: string;
  provider: TTSProviderId;
  // false = free (Microsoft). true = advanced (Gemini today), hidden unless ENABLE_ADVANCED_TTS.
  advanced: boolean;
  gender: VoiceGender;
  useCases: string[];
}

// One accent option: the locale sent to TTS, with the providers that support it (so the dropdown
// shows an accent only when the selected voice's provider can do it). Gemini prompts stay backend.
export interface AccentOption {
  locale: string;
  label: string;
  base: string;
  providers: TTSProviderId[];
}

export interface TTSCatalog {
  voices: VoiceDescriptor[];
  accents: AccentOption[];
}

// The catalog, gated by the tenant's advanced-TTS flag (advanced voices hidden unless enabled).
// Pure function over static data — no store, no network.
export const getVoiceCatalog = (advancedEnabled: boolean): TTSCatalog => ({
  voices: TTS_VOICES.filter((v) => !v.advanced || advancedEnabled),
  accents: TTS_ACCENTS
});

// Provider of a given voice (from the full catalog, regardless of gating). Drives synthesis routing.
export const getProviderForVoice = (voiceId: string): string =>
  TTS_VOICES.find((v) => v.id === voiceId)?.provider || "";

// Voices proposed when the user has not picked one yet
const DEFAULT_ADVANCED_VOICE_ID = "Kore";
const DEFAULT_STANDARD_VOICE_ID = "en-US-AmandaMultilingualNeural";

// Id of the voice to propose by default
export const getDefaultVoiceId = (advancedEnabled: boolean): string => {
  const voices = getVoiceCatalog(advancedEnabled).voices;
  const wanted = advancedEnabled ? DEFAULT_ADVANCED_VOICE_ID : DEFAULT_STANDARD_VOICE_ID;
  const preferred = voices.find((v) => v.id === wanted)
    || voices.find((v) => v.advanced === advancedEnabled)
    || voices[0];
  return preferred ? preferred.id : "";
};

// ── Microsoft voices (free) ──────────────────────────────────────────────────────────────────
const MICROSOFT_VOICES: VoiceDescriptor[] = [
  { id: "en-US-AdamMultilingualNeural", label: "Adam", gender: "MALE", useCases: ["Warm", "Expressive"] },
  { id: "en-US-AmandaMultilingualNeural", label: "Amanda", gender: "FEMALE", useCases: ["Bright", "Expressive"] },
  { id: "en-US-AndrewMultilingualNeural", label: "Andrew", gender: "MALE", useCases: ["Warm", "Expressive"] },
  { id: "en-US-AvaMultilingualNeural", label: "Ava", gender: "FEMALE", useCases: ["Bright", "Expressive"] },
  { id: "en-US-BrandonMultilingualNeural", label: "Brandon", gender: "MALE", useCases: ["Gravelly", "Expressive"] },
  { id: "en-US-BrianMultilingualNeural", label: "Brian", gender: "MALE", useCases: ["Casual", "Expressive"] },
  { id: "en-US-ChristopherMultilingualNeural", label: "Christopher", gender: "MALE", useCases: ["Firm", "Professional"] },
  { id: "en-US-CoraMultilingualNeural", label: "Cora", gender: "FEMALE", useCases: ["Mature", "Professional"] },
  { id: "en-US-DavisMultilingualNeural", label: "Davis", gender: "MALE", useCases: ["Even", "Professional"] },
  { id: "en-US-DerekMultilingualNeural", label: "Derek", gender: "MALE", useCases: ["Friendly", "Expressive"] },
  { id: "en-US-DustinMultilingualNeural", label: "Dustin", gender: "MALE", useCases: ["Youthful", "Expressive"] },
  { id: "en-US-EmmaMultilingualNeural", label: "Emma", gender: "FEMALE", useCases: ["Upbeat", "Expressive"] },
  { id: "en-US-EvelynMultilingualNeural", label: "Evelyn", gender: "FEMALE", useCases: ["Clear", "Professional"] },
  { id: "en-US-LewisMultilingualNeural", label: "Lewis", gender: "MALE", useCases: ["Informative", "Professional"] },
  { id: "en-US-LolaMultilingualNeural", label: "Lola", gender: "FEMALE", useCases: ["Smooth", "Expressive"] },
  { id: "en-US-NancyMultilingualNeural", label: "Nancy", gender: "FEMALE", useCases: ["Bright", "Expressive"] },
  { id: "en-US-PhoebeMultilingualNeural", label: "Phoebe", gender: "FEMALE", useCases: ["Soft", "Expressive"] },
  { id: "en-US-SamuelMultilingualNeural", label: "Samuel", gender: "MALE", useCases: ["Knowledgeable", "Professional"] },
  { id: "en-US-SerenaMultilingualNeural", label: "Serena", gender: "FEMALE", useCases: ["Smooth", "Professional"] },
  { id: "en-US-SteffanMultilingualNeural", label: "Steffan", gender: "MALE", useCases: ["Firm", "Professional"] },
  { id: "en-US-AlloyTurboMultilingualNeural", label: "Alloy", gender: "MALE", useCases: ["Even", "Professional"] },
  { id: "en-US-EchoTurboMultilingualNeural", label: "Echo", gender: "MALE", useCases: ["Smooth", "Professional"] },
  { id: "en-US-FableTurboMultilingualNeural", label: "Fable", gender: "NEUTRAL", useCases: ["Lively", "Expressive"] },
  { id: "en-US-NovaTurboMultilingualNeural", label: "Nova", gender: "FEMALE", useCases: ["Bright", "Expressive"] },
  { id: "en-US-OnyxTurboMultilingualNeural", label: "Onyx", gender: "MALE", useCases: ["Firm", "Professional"] },
  { id: "en-US-ShimmerTurboMultilingualNeural", label: "Shimmer", gender: "FEMALE", useCases: ["Warm", "Professional"] },
  { id: "en-GB-AdaMultilingualNeural", label: "Ada", gender: "FEMALE", useCases: ["Clear", "Professional"] },
  { id: "en-GB-OllieMultilingualNeural", label: "Ollie", gender: "MALE", useCases: ["Easy-going", "Expressive"] },
  { id: "de-DE-SeraphinaMultilingualNeural", label: "Seraphina", gender: "FEMALE", useCases: ["Bright", "Expressive"] },
  { id: "de-DE-FlorianMultilingualNeural", label: "Florian", gender: "MALE", useCases: ["Informative", "Professional"] },
  { id: "es-ES-ArabellaMultilingualNeural", label: "Arabella", gender: "FEMALE", useCases: ["Warm", "Expressive"] },
  { id: "es-ES-IsidoraMultilingualNeural", label: "Isidora", gender: "FEMALE", useCases: ["Lively", "Expressive"] },
  { id: "es-ES-TristanMultilingualNeural", label: "Tristan", gender: "MALE", useCases: ["Firm", "Professional"] },
  { id: "es-ES-XimenaMultilingualNeural", label: "Ximena", gender: "FEMALE", useCases: ["Friendly", "Expressive"] },
  { id: "fr-FR-LucienMultilingualNeural", label: "Lucien", gender: "MALE", useCases: ["Clear", "Professional"] },
  { id: "fr-FR-VivienneMultilingualNeural", label: "Vivienne", gender: "FEMALE", useCases: ["Warm", "Expressive"] },
  { id: "fr-FR-RemyMultilingualNeural", label: "Remy", gender: "MALE", useCases: ["Casual", "Expressive"] },
  { id: "it-IT-AlessioMultilingualNeural", label: "Alessio", gender: "MALE", useCases: ["Lively", "Expressive"] },
  { id: "it-IT-GiuseppeMultilingualNeural", label: "Giuseppe", gender: "MALE", useCases: ["Warm", "Expressive"] },
  { id: "it-IT-IsabellaMultilingualNeural", label: "Isabella", gender: "FEMALE", useCases: ["Bright", "Expressive"] },
  { id: "it-IT-MarcelloMultilingualNeural", label: "Marcello", gender: "MALE", useCases: ["Smooth", "Professional"] },
  { id: "ja-JP-MasaruMultilingualNeural", label: "Masaru", gender: "MALE", useCases: ["Firm", "Professional"] },
  { id: "ko-KR-HyunsuMultilingualNeural", label: "Hyunsu", gender: "MALE", useCases: ["Friendly", "Expressive"] },
  { id: "pt-BR-MacerioMultilingualNeural", label: "Macerio", gender: "MALE", useCases: ["Warm", "Expressive"] },
  { id: "pt-BR-ThalitaMultilingualNeural", label: "Thalita", gender: "FEMALE", useCases: ["Bright", "Expressive"] },
  { id: "zh-CN-XiaoxiaoMultilingualNeural", label: "Xiaoxiao", gender: "FEMALE", useCases: ["Lively", "Expressive"] },
  { id: "zh-CN-XiaochenMultilingualNeural", label: "Xiaochen", gender: "FEMALE", useCases: ["Casual", "Expressive"] },
  { id: "zh-CN-XiaoyuMultilingualNeural", label: "Xiaoyu", gender: "FEMALE", useCases: ["Clear", "Professional"] },
  { id: "zh-CN-YunyiMultilingualNeural", label: "Yunyi", gender: "MALE", useCases: ["Informative", "Professional"] },
  { id: "zh-CN-YunfanMultilingualNeural", label: "Yunfan", gender: "MALE", useCases: ["Clear", "Professional"] },
  { id: "zh-CN-YunxiaoMultilingualNeural", label: "Yunxiao", gender: "MALE", useCases: ["Knowledgeable", "Professional"] },
].map((v) => ({ ...v, provider: "MICROSOFT" as TTSProviderId, advanced: false, gender: v.gender as VoiceGender }));

// ── Gemini voices (advanced) ─────────────────────────────────────────────────────────────────
const GEMINI_VOICES: VoiceDescriptor[] = [
  { id: "Zephyr", gender: "FEMALE", useCases: ["Bright", "Expressive"] },
  { id: "Puck", gender: "MALE", useCases: ["Upbeat", "Expressive"] },
  { id: "Charon", gender: "MALE", useCases: ["Informative", "Professional"] },
  { id: "Kore", gender: "FEMALE", useCases: ["Firm", "Professional"] },
  { id: "Fenrir", gender: "MALE", useCases: ["Excitable", "Expressive"] },
  { id: "Leda", gender: "FEMALE", useCases: ["Youthful", "Expressive"] },
  { id: "Orus", gender: "MALE", useCases: ["Firm", "Professional"] },
  { id: "Aoede", gender: "FEMALE", useCases: ["Breezy", "Expressive"] },
  { id: "Callirrhoe", gender: "FEMALE", useCases: ["Easy-going", "Expressive"] },
  { id: "Autonoe", gender: "FEMALE", useCases: ["Bright", "Expressive"] },
  { id: "Enceladus", gender: "MALE", useCases: ["Breathy", "Professional"] },
  { id: "Iapetus", gender: "MALE", useCases: ["Clear", "Professional"] },
  { id: "Umbriel", gender: "MALE", useCases: ["Easy-going", "Expressive"] },
  { id: "Algieba", gender: "MALE", useCases: ["Smooth", "Professional"] },
  { id: "Despina", gender: "FEMALE", useCases: ["Smooth", "Professional"] },
  { id: "Erinome", gender: "FEMALE", useCases: ["Clear", "Professional"] },
  { id: "Algenib", gender: "MALE", useCases: ["Gravelly", "Professional"] },
  { id: "Rasalgethi", gender: "MALE", useCases: ["Informative", "Professional"] },
  { id: "Laomedeia", gender: "FEMALE", useCases: ["Upbeat", "Expressive"] },
  { id: "Achernar", gender: "FEMALE", useCases: ["Soft", "Professional"] },
  { id: "Alnilam", gender: "MALE", useCases: ["Firm", "Professional"] },
  { id: "Schedar", gender: "MALE", useCases: ["Even", "Professional"] },
  { id: "Gacrux", gender: "FEMALE", useCases: ["Mature", "Expressive"] },
  { id: "Pulcherrima", gender: "FEMALE", useCases: ["Forward", "Expressive"] },
  { id: "Achird", gender: "MALE", useCases: ["Friendly", "Expressive"] },
  { id: "Zubenelgenubi", gender: "MALE", useCases: ["Casual", "Expressive"] },
  { id: "Vindemiatrix", gender: "FEMALE", useCases: ["Gentle", "Expressive"] },
  { id: "Sadachbia", gender: "MALE", useCases: ["Lively", "Expressive"] },
  { id: "Sadaltager", gender: "MALE", useCases: ["Knowledgeable", "Professional"] },
  { id: "Sulafat", gender: "FEMALE", useCases: ["Warm", "Professional"] },
].map((v) => ({ id: v.id, label: v.id, provider: "GEMINI" as TTSProviderId, advanced: true, gender: v.gender as VoiceGender, useCases: v.useCases }));

export const TTS_VOICES: VoiceDescriptor[] = [...MICROSOFT_VOICES, ...GEMINI_VOICES];

// ── Accents ──────────────────────────────────────────────────────────────────────────────────
// Real BCP-47 locales work on both providers. Private-use tokens (cross-language) are Gemini-only.
const BOTH: TTSProviderId[] = ["MICROSOFT", "GEMINI"];
const GEMINI_ONLY: TTSProviderId[] = ["GEMINI"];

export const TTS_ACCENTS: AccentOption[] = [
  // English
  { locale: "en-US", label: "American", base: "en", providers: BOTH },
  { locale: "en-GB", label: "British", base: "en", providers: BOTH },
  { locale: "en-AU", label: "Australian", base: "en", providers: BOTH },
  { locale: "en-IE", label: "Irish", base: "en", providers: BOTH },
  { locale: "en-CA", label: "Canadian", base: "en", providers: BOTH },
  { locale: "en-IN", label: "Indian", base: "en", providers: BOTH },
  { locale: "en-x-spanish", label: "Spanish (non-native)", base: "en", providers: GEMINI_ONLY },
  { locale: "en-x-french", label: "French (non-native)", base: "en", providers: GEMINI_ONLY },
  // French
  { locale: "fr-FR", label: "Français", base: "fr", providers: BOTH },
  { locale: "fr-CA", label: "Québécois", base: "fr", providers: BOTH },
  { locale: "fr-BE", label: "Belge", base: "fr", providers: BOTH },
  // Other languages
  { locale: "es-ES", label: "Castilian", base: "es", providers: BOTH },
  { locale: "es-MX", label: "Mexican", base: "es", providers: BOTH },
  { locale: "de-DE", label: "German", base: "de", providers: BOTH },
  { locale: "it-IT", label: "Italian", base: "it", providers: BOTH },
  { locale: "pt-BR", label: "Brazilian", base: "pt", providers: BOTH },
  { locale: "pt-PT", label: "European", base: "pt", providers: BOTH },
  { locale: "ja-JP", label: "Japanese", base: "ja", providers: BOTH },
  { locale: "ko-KR", label: "Korean", base: "ko", providers: BOTH },
  { locale: "zh-CN", label: "Mandarin (Beijing)", base: "zh", providers: BOTH },
  { locale: "zh-TW", label: "Mandarin (Taiwan)", base: "zh", providers: BOTH },
  { locale: "zh-HK", label: "Cantonese", base: "zh", providers: BOTH },
];
